import { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useGetUsersQuery, usersApi } from 'redux/features/users/userApi';
import { useDispatch } from 'react-redux';
import { getSocket } from 'lib/socket';
import TableSkeleton from 'components/TableSkeleton';
import useUser from 'hooks/useUser';

import { useActiveInactiveUserMutation } from 'redux/features/users/authApi';
import { toast } from 'react-toastify';
import Swal from 'sweetalert2';
import dayjs from 'lib/dayjs';
import { matchesNameSearch } from 'utils/common';
import Icon from '../../components/AppIcon';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import CommandPalette from '../../components/ui/CommandPalette';
import Switch from '../../components/ui/Switch';
import { formatTime, formatHoursMinutes } from '../../utils/timeTracker';
import AddEmployeeModal from './components/AddEmployeeModal';
import LogModal from '../../components/LogModal';
import LunchBreakTrackerList from '../../components/ui/LunchBreakTrackerList';

const EmployeeManagement = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    role: 'all',
    session: 'all',
    status: 'Active',
  });
  const [employees, setEmployees] = useState([]);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showSessionModal, setShowSessionModal] = useState(false);
  const [selectedUserForSessions, setSelectedUserForSessions] = useState(null);
  const [activeInactiveUser, { isLoading: isUpdatingUserStatus }] = useActiveInactiveUserMutation();
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [lunchModalEmployee, setLunchModalEmployee] = useState(null);
  const dispatch = useDispatch();
  const user = useUser();

  const { data, isLoading } = useGetUsersQuery();
  useEffect(() => {
    setEmployees(data?.data?.users);
  }, [data]);

  const handleActiveInactiveUser = async (id) => {
    const employee = employees?.find((e) => e.id === id);
    const name = employee ? `${employee.first_name} ${employee.last_name}` : 'this user';
    const newStatus = employee?.is_active ? 'Inactive' : 'Active';

    const result = await Swal.fire({
      title: `Are you sure?`,
      text: `You want to mark ${name} as ${newStatus}`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: newStatus === 'Active' ? '#22c55e' : '#ef4444',
      cancelButtonColor: '#6b7280',
      confirmButtonText: `Yes, make ${newStatus}`,
      cancelButtonText: 'Cancel',
    });

    if (!result.isConfirmed) return;

    try {
      const response = await activeInactiveUser(id).unwrap();
      console.log(response);
      if (response?.status === 'success') {
        toast.success(`User marked as ${newStatus} successfully`);
        dispatch(
          usersApi.util.updateQueryData('getUsers', undefined, (draft) => {
            const users = draft?.data?.users || draft;
            const user = users.find((u) => u.id === id);
            if (user) {
              user.is_active = !user.is_active;
            }
          }),
        );
      }
    } catch (error) {
      console.log(error);
    }
  };

  const updateSessionStatus = useCallback(
    (payload, status) => {
      dispatch(
        usersApi.util.updateQueryData('getUsers', undefined, (draft) => {
          const users = draft?.data?.users || draft;
          const user = users.find((u) => u.id === payload?.user_id);
          if (user) {
            user.sessionStatus = status;
            user.lastCheckOut = payload?.check_out;
            user.lastCheckIn = payload?.check_in;

            if (status === 'Inactive') {
              user.todayHours = user?.todayHours + (payload?.total_hours || 0);
            }
          }
        }),
      );
    },
    [dispatch],
  );

  const socket = getSocket();
  useEffect(() => {
    if (!socket) return;

    socket.on('userCheckedIn', (payload) => {
      console.log(payload);
      updateSessionStatus(payload, 'Active');
    });
    socket.on('userCheckedOut', (payload) => {
      updateSessionStatus(payload, 'Inactive');
    });

    return () => {
      socket.off('userCheckedIn');
      socket.off('userCheckedOut');
    };
  }, [dispatch, updateSessionStatus, socket]);

  const handleEditClick = (employee, e) => {
    e?.stopPropagation();
    navigate(`/employee-management/employee-edit-full-page?id=${employee?.id}`);
  };

  const handleFilterChange = (key, value) => {
    setFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const filteredEmployees = employees?.filter((employee) => {
    const matchesSearch = matchesNameSearch(employee, searchTerm);

    const matchesRole =
      filters.role === 'all' ||
      (filters.role === 'ADMIN' && employee?.role === 'ADMIN') ||
      (filters.role === 'EMPLOYEE' && employee?.role === 'EMPLOYEE');

    const matchesSession =
      filters.session === 'all' ||
      (filters.session === 'Active' && employee?.sessionStatus === 'Active') ||
      (filters.session === 'Inactive' && employee?.sessionStatus === 'Inactive');

    const matchesStatus =
      filters.status === 'all' ||
      (filters.status === 'Active' && employee?.is_active) ||
      (filters.status === 'Inactive' && !employee?.is_active);

    return matchesSearch && matchesRole && matchesSession && matchesStatus;
  });

  const handleUserDoubleClick = (employee) => {
    navigate(`/employee-management/employee-time-logs?id=${employee?.id}`);
  };

  if (!user) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 },
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-primary-50 via-background to-secondary-50">
      <Header />
      <Sidebar />
      <CommandPalette />

      <main className="lg:ml-60 pt-16">
        <motion.div
          className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {/* Summary Statistics */}
          <motion.div
            className="grid grid-cols-1 md:grid-cols-3  gap-3 mb-3"
            variants={itemVariants}
          >
            <div className="bg-surface rounded-md border border-border p-5 shadow-sm  transition-all">
              <div className="flex items-center">
                <div className="p-3 bg-primary-50 rounded-lg">
                  <Icon name="Users" size={20} className="text-primary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                    Total Active Employees
                  </p>
                  <p className="text-2xl font-bold text-text-primary">
                    {employees?.filter((emp) => emp?.is_active === true)?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-md border border-border p-5 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center">
                <div className="p-3 bg-success-50 rounded-lg">
                  <Icon name="CheckCircle" size={20} className="text-success-600" />
                </div>
                <div className="ml-4">
                  <p className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                    Active Sessions
                  </p>
                  <p className="text-2xl font-bold text-text-primary">
                    {employees?.filter((emp) => emp?.sessionStatus === 'Active')?.length}
                  </p>
                </div>
              </div>
            </div>

            <div className="bg-surface rounded-md border border-border p-5 shadow-sm hover:shadow-md transition-all">
              <div className="flex items-center">
                <div className="p-3 bg-secondary-50 rounded-lg">
                  <Icon name="XCircle" size={20} className="text-secondary-600" />
                </div>
                <div className="ml-4">
                  <p className="text-xs font-bold text-text-secondary uppercase tracking-wider">
                    Inactive Sessions
                  </p>
                  <p className="text-2xl font-bold text-text-primary">
                    {
                      employees?.filter(
                        (emp) =>
                          emp?.sessionStatus === 'Inactive' &&
                          emp?.role !== 'ADMIN' &&
                          emp?.is_active === true,
                      )?.length
                    }
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
          {/* User Management Section */}
          <motion.div
            className="bg-surface rounded-md border border-border shadow-sm mb-8 overflow-hidden"
            variants={itemVariants}
          >
            <div className="px-6 py-5 border-b border-border bg-surface">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-xl font-bold text-text-primary flex items-center space-x-2">
                    <Icon name="Users" size={24} className="text-primary" />
                    <span>Employee Management</span>
                  </h2>
                  <p className="text-sm text-text-secondary mt-1">
                    Manage your workforce and view detailed activity logs
                  </p>
                </div>
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center space-x-2 px-4 py-2.5 bg-primary text-white rounded-xl hover:bg-primary-600 transition-all duration-200 shadow-sm hover:shadow-md active:scale-95"
                >
                  <Icon name="UserPlus" size={18} />
                  <span className="hidden sm:inline font-semibold">Add Employee</span>
                </button>
              </div>
            </div>

            <div className="p-6">
              {/* Search and Filter Controls */}
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-6">
                {/* Search Box */}
                <div className="relative flex-1 max-w-md">
                  <Icon
                    name="Search"
                    size={20}
                    className="absolute left-3 top-1/2 transform -translate-y-1/2 text-text-secondary"
                  />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e?.target?.value)}
                    placeholder="Search by Name or Employee ID"
                    className="w-full pl-10 pr-4 py-1 bg-background border border-border rounded-xl focus:ring-2 focus:ring-primary-500 focus:border-primary-500 transition-all"
                  />
                </div>

                {/* Filter Options */}

                <div className="flex flex-row items-center gap-3">
                  <span className="text-sm font-semibold text-text-secondary">Filter</span>
                  <div className="flex flex-wrap items-center gap-3">
                    <div className="flex items-center rounded-lg overflow-hidden border border-border bg-background transition-colors focus-within:border-primary/50">
                      <select
                        value={filters.role}
                        onChange={(e) => handleFilterChange('role', e.target.value)}
                        className="border-none focus:ring-0 text-sm py-1 pl-2 pr-8 bg-transparent outline-none text-text-primary font-medium"
                      >
                        <option value="all">All Roles</option>
                        <option value="ADMIN">Admin</option>
                        <option value="EMPLOYEE">Employee</option>
                      </select>
                    </div>

                    {/* User Session Dropdown */}
                    <div className="flex items-center rounded-lg overflow-hidden border border-border bg-background transition-colors focus-within:border-primary/50">
                      <select
                        value={filters.session}
                        onChange={(e) => handleFilterChange('session', e.target.value)}
                        className="border-none focus:ring-0 text-sm py-1 pl-2 pr-8 bg-transparent outline-none text-text-primary font-medium"
                      >
                        <option value="all">All Sessions</option>
                        <option value="Active">Checked In</option>
                        <option value="Inactive">Checked Out</option>
                      </select>
                    </div>

                    {/* User Status Switch */}
                    <div className="flex items-center gap-2">
                      <span className="text-sm font-semibold text-text-primary">
                        {filters.status === 'Active' ? 'Active' : 'Inactive'}
                      </span>
                      <Switch
                        checked={filters.status === 'Active'}
                        onChange={(checked) =>
                          handleFilterChange('status', checked ? 'Active' : 'Inactive')
                        }
                        size="default"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* User List Table */}
              <div className="overflow-x-auto max-h-[550px] overflow-y-auto">
                <table className="min-w-full divide-y divide-border">
                  <thead className="bg-white sticky top-0 z-10 ">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">
                        <span className="hidden sm:inline">Employee ID</span>
                        <span className="sm:hidden">ID</span>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">
                        <span className="hidden sm:inline">Role</span>
                        <span className="sm:hidden">Role</span>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">
                        <span className="hidden sm:inline">Employee Name</span>
                        <span className="sm:hidden">NAME</span>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">
                        <span className="hidden sm:inline"> In</span>
                        <span className="sm:hidden">IN</span>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">
                        <span className="hidden sm:inline"> Out</span>
                        <span className="sm:hidden">OUT</span>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">
                        <span className="hidden sm:inline"> Time</span>
                        <span className="sm:hidden">TIME</span>
                      </th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-text-secondary uppercase tracking-wider">
                        Status
                      </th>
                      <th className="px-6 py-4 text-right text-xs font-bold text-text-secondary uppercase tracking-wider pr-10">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  {isLoading ? (
                    <TableSkeleton columns={7} rows={3} />
                  ) : (
                    <tbody className="bg-surface divide-y divide-border">
                      {filteredEmployees?.length === 0 ? (
                        <tr>
                          <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                            <Icon
                              name="Users"
                              size={48}
                              color="var(--color-secondary-300)"
                              className="mx-auto mb-4"
                            />
                            <p className="text-lg font-medium mb-2">
                              No employees found matching your filters
                            </p>
                            <p className="text-sm">Try adjusting your search or filter criteria.</p>
                          </td>
                        </tr>
                      ) : (
                        filteredEmployees?.map((employee) => (
                          <tr
                            key={employee?.employee_id}
                            className="hover:bg-primary-50/50 cursor-pointer transition-all duration-200 border-b border-border last:border-0"
                            onDoubleClick={() => handleUserDoubleClick(employee)}
                            title="Double-click to view detailed time logs"
                          >
                            <td className="px-6 py-4 whitespace-nowrap text-sm font-mono text-text-secondary">
                              #{employee?.employee_id}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <span
                                className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
                                  employee?.role === 'ADMIN'
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-slate-100 text-slate-700'
                                }`}
                              >
                                {employee?.role}
                              </span>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <div className="flex items-center space-x-3">
                                <div className="relative">
                                  <div className="flex-shrink-0 h-8 w-8 bg-primary-100 rounded-full flex items-center justify-center">
                                    <span className="text-primary-600 font-medium text-sm">
                                      {employee?.first_name?.charAt(0)}
                                    </span>
                                  </div>
                                  {/* Session Status Indicator */}
                                  <div
                                    className={`absolute -bottom-1 -right-1 w-3 h-3 rounded-full border-2 border-white ${
                                      employee?.sessionStatus === 'Active'
                                        ? 'bg-green-500 animate-pulse'
                                        : 'bg-red-500'
                                    }`}
                                    title={
                                      employee?.sessionStatus === 'Active'
                                        ? 'Currently Checked In'
                                        : `Last Checkout : ${dayjs(employee?.lastCheckOut).fromNow()}`
                                    }
                                  ></div>
                                </div>
                                <div>
                                  <div className="text-sm font-bold text-text-primary">
                                    {employee?.first_name + ' ' + employee?.last_name}
                                  </div>
                                  <div className="text-xs text-text-secondary">
                                    {employee?.email}
                                  </div>
                                </div>
                              </div>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary font-medium">
                              {employee?.lastCheckIn ? formatTime(employee?.lastCheckIn) : '--:--'}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-text-primary font-medium">
                              {employee?.lastCheckOut ? (
                                formatTime(employee?.lastCheckOut)
                              ) : employee?.sessionStatus === 'Active' ? (
                                <span className="text-success-600 font-bold">Active</span>
                              ) : (
                                '--:--'
                              )}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-sm text-primary-700 font-bold">
                              {formatHoursMinutes(employee?.todayHours)}
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleActiveInactiveUser(employee?.id);
                                  setSelectedUserId(employee?.id);
                                }}
                                className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold transition-all duration-200 ${
                                  employee?.is_active
                                    ? 'bg-success-50 text-success-700 border border-success-100 hover:bg-success-100'
                                    : 'bg-error-50 text-error-700 border border-error-100 hover:bg-error-100'
                                }`}
                                title="Change Status"
                              >
                                {isUpdatingUserStatus && selectedUserId === employee?.id ? (
                                  <Icon name="Loader" className="animate-spin" size={14} />
                                ) : (
                                  <Icon
                                    name={employee?.is_active ? 'CheckCircle' : 'XCircle'}
                                    size={14}
                                  />
                                )}
                                <span>{employee?.is_active ? 'Active' : 'Inactive'}</span>
                              </button>
                            </td>
                            <td className="px-6 py-4 whitespace-nowrap text-right">
                              <div className="flex items-center justify-end space-x-2">
                                <button
                                  onClick={(e) => {
                                    handleEditClick(employee, e);
                                  }}
                                  className="p-2 text-primary-600 bg-primary-50 rounded-xl hover:bg-primary-100 transition-colors"
                                  title="Edit employee"
                                >
                                  <Icon name="Edit" size={18} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedUserForSessions(employee);
                                    setShowSessionModal(true);
                                  }}
                                  className="p-2 text-secondary-600 bg-secondary-100 rounded-xl hover:bg-secondary-200 transition-colors"
                                  title="View Login History"
                                >
                                  <Icon name="History" size={18} />
                                </button>
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setLunchModalEmployee(employee);
                                  }}
                                  className="p-2 text-orange-600 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors"
                                  title="View Lunch Break Records"
                                >
                                  <Icon name="UtensilsCrossed" size={18} />
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  )}
                </table>
              </div>

              {filteredEmployees?.length > 0 && (
                <div className="mt-6 flex items-center justify-between px-2">
                  <p className="text-sm font-medium text-text-secondary">
                    Showing{' '}
                    <span className="text-text-primary font-bold">{filteredEmployees?.length}</span>{' '}
                    of <span className="text-text-primary font-bold">{employees?.length}</span>{' '}
                    employees
                  </p>
                  <div className="text-xs font-medium text-text-secondary flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-success-500 animate-pulse"></div>
                    Real-time updates active
                  </div>
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </main>

      <AddEmployeeModal isOpen={showAddModal} onClose={() => setShowAddModal(false)} />

      <LogModal
        isOpen={showSessionModal}
        onClose={() => {
          setShowSessionModal(false);
          setSelectedUserForSessions(null);
        }}
        user={selectedUserForSessions}
      />

      {/* Lunch Break Tracker Modal */}
      {lunchModalEmployee && (
        <div className="fixed inset-0 z-50">
          <div className="fixed inset-0 bg-black/50" onClick={() => setLunchModalEmployee(null)} />
          <div className="flex h-full items-center justify-center p-4">
            <div className="relative w-full max-w-4xl bg-white rounded-2xl shadow-2xl z-10 max-h-[85vh] flex flex-col overflow-hidden border border-gray-100">
              <div className="flex items-center justify-between p-5 border-b border-gray-100 shrink-0 bg-white">
                <div className="flex items-center gap-4">
                  <div className="p-2.5 rounded-xl bg-orange-50/80">
                    <Icon name="UtensilsCrossed" size={22} className="text-orange-600" />
                  </div>
                  <div>
                    <h3 className="text-lg font-bold text-gray-900">Lunch Break Records</h3>
                    <p className="text-[10px] text-gray-400 font-mono uppercase tracking-wider mt-0.5">
                      {lunchModalEmployee?.first_name} {lunchModalEmployee?.last_name} ·{' '}
                      {lunchModalEmployee?.employee_id}
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setLunchModalEmployee(null)}
                  className="p-2 rounded-xl text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-all active:scale-95 outline-none focus:outline-none"
                >
                  <Icon name="X" size={20} />
                </button>
              </div>
              <div className="p-0 overflow-y-auto flex-1 bg-white">
                <div className="p-5">
                  <LunchBreakTrackerList
                    employeeId={lunchModalEmployee?.id}
                    isAdmin={true}
                    showDateFilter={false}
                    showMonthFilter={true}
                    compact={false}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmployeeManagement;
